import defaultStyles from "fond/draw/defaultStyles";
import { MapboxStyleLayer } from "fond/types";

type BaseSymbolStyle = Pick<mapboxgl.SymbolLayer, "layout" | "paint">;

const baseSymbolStyle = {
  layout: {
    "text-field": ["case", ["==", ["get", "user_excluded"], "true"], "\uE85D", "\uE85C"],
    "text-anchor": "bottom",
    "text-justify": "center",
    // Note we prevent overlap and ignore placement to ensure only one icon is shown per feature location,
    // however take note that icons not rendered due to overlap are not returned in mapbox queryRenderedFeatures calls.
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-font": ["fontello Regular"],
    "text-padding": 0,
    "text-rotate": 0.01,
    "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 16],
    "text-line-height": -0.45,
  },
  paint: {
    "text-color": ["case", ["==", ["get", "user_excluded"], "true"], "red", "green"],
    "text-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, 1],
    "text-translate": [-0.6, 0.1],
    "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 2],
    "text-halo-color": "#ffffff",
  },
} as BaseSymbolStyle;

const baseHeatMapSymbolStyle = {
  ...baseSymbolStyle,
  layout: {
    ...baseSymbolStyle.layout,
    "text-field": ["case", ["==", ["get", "user_excluded"], "true"], "\uE80D", "\uE80A"],
  },
  paint: {
    ...baseSymbolStyle.paint,
    "text-color": "white",
    "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 1],
    "text-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 18, 1],
  },
} as BaseSymbolStyle;

/**
 * The MapboxDraw theme extended to include styles specific to planner projects
 * when drawing address features.
 */
const getDefaults = (baseStyle: BaseSymbolStyle, withHeatmap: boolean): MapboxStyleLayer[] =>
  defaultStyles.map((style) => {
    if (style.id === "gl-draw-point-stroke-active") {
      return {
        ...style,
        paint: {
          ...style.paint,
          ...(withHeatmap
            ? {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 12],
                "circle-color": "rgba(255, 255, 255, 0)",
                "circle-stroke-color": "#FFF000",
                "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 14, 1, 18, 3],
              }
            : {
                "circle-radius": 12,
                "circle-color": "#FFF000",
              }),
        },
      } as mapboxgl.CircleLayer;
    }

    if (style.id === "gl-draw-point-point-stroke-inactive") {
      return {
        ...style,
        paint: {
          ...style.paint,
          "circle-radius": 0,
        },
      } as mapboxgl.CircleLayer;
    }

    if (["gl-draw-point-active", "gl-draw-point-inactive"].includes(style.id)) {
      return {
        ...style,
        type: "symbol",
        layout: {
          ...baseStyle.layout,
        },
        paint: {
          ...baseStyle.paint,
        },
      } as mapboxgl.SymbolLayer;
    }

    return style;
  });

export const createStyles = ({
  styleId,
  source,
  sourceLayer,
  layerFilter,
  withHeatmap = false,
}: {
  styleId: string;
  source: string;
  sourceLayer: string;
  layerFilter: any[] | undefined;
  withHeatmap?: boolean;
}): MapboxStyleLayer[] => {
  const baseStyle = withHeatmap ? baseHeatMapSymbolStyle : baseSymbolStyle;
  const defaults = getDefaults(baseStyle, withHeatmap);
  return [
    {
      id: styleId,
      type: "symbol",
      source,
      "source-layer": sourceLayer,
      ...(layerFilter ? { filter: layerFilter } : {}),
      ...baseStyle,
    } as mapboxgl.SymbolLayer,
    ...defaults,
  ];
};

// Creates styles for the exclude draw mode based on the styles used for coloring by CTS.
// We rely on the CTS Styles (heatmap) to have the same properties.FondID as the underlying service location.
export const getCTSStyles = (map: mapboxgl.Map, colorBy: string, excludedFondIDs: string[]): mapboxgl.CircleLayer[] => {
  const currentCTSStyles = map?.getStyle().layers?.filter((layer) => layer.metadata?.layerKey === colorBy) as mapboxgl.CircleLayer[];

  return (
    currentCTSStyles?.map(
      (style) =>
        ({
          ...style,
          id: `${style.id}-exclude`,
          metadata: { ...(style.metadata || {}), isExcludeLayer: true },
          paint: {
            ...style.paint,
            "circle-color": ["case", ["in", ["get", "FondID"], ["literal", excludedFondIDs]], "rgba(0,0,0, 0.5)", style?.paint?.["circle-color"]],
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 12],
            "circle-stroke-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 1],
            "circle-stroke-color": "#FFF",
            "circle-stroke-width": ["case", ["in", ["get", "FondID"], ["literal", excludedFondIDs]], 0, 1],
          },
          layout: { ...style.layout, visibility: "visible" },
        }) as mapboxgl.CircleLayer
    ) || []
  );
};

// Remove any existing CTS styles
export const removeCTSStyles = (map: mapboxgl.Map): void => {
  map
    ?.getStyle()
    .layers?.filter((layer) => layer.metadata?.isExcludeLayer === true)
    .forEach((layer) => {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    });
};
